import React, { useState } from 'react';

interface Employee {
  id: number;
  name: string;
  email: string;
  phone: string;
  position: string;
  department: 'technician' | 'sales' | 'admin' | 'management';
  hireDate: string;
  salary: string;
  status: 'active' | 'inactive' | 'on-leave';
  certifications: string[];
  completedJobs: number;
  rating: number;
  address: string;
  emergencyContact: string;
  notes: string;
}

const EmployeesPage: React.FC = () => {
  const [employees] = useState<Employee[]>([
    {
      id: 1,
      name: 'Mike Johnson',
      email: 'mike.johnson@hvaccrm.com',
      phone: '(555) 111-1111',
      position: 'Senior HVAC Technician',
      department: 'technician',
      hireDate: '2022-03-15',
      salary: '$65,000',
      status: 'active',
      certifications: ['EPA 608', 'NATE Certified', 'Heat Pump Specialist'],
      completedJobs: 127,
      rating: 4.8,
      address: '123 Tech Lane, City, ST 12345',
      emergencyContact: 'Jane Johnson - (555) 111-2222',
      notes: 'Excellent with commercial installations. Available for overtime.'
    },
    {
      id: 2,
      name: 'David Lee',
      email: 'david.lee@hvaccrm.com',
      phone: '(555) 222-2222',
      position: 'HVAC Technician',
      department: 'technician',
      hireDate: '2023-01-10',
      salary: '$55,000',
      status: 'active',
      certifications: ['EPA 608', 'OSHA 30'],
      completedJobs: 89,
      rating: 4.6,
      address: '456 Service Ave, City, ST 23456',
      emergencyContact: 'Maria Lee - (555) 222-3333',
      notes: 'Specializes in residential repairs. Fast learner.'
    },
    {
      id: 3,
      name: 'Tom Anderson',
      email: 'tom.anderson@hvaccrm.com',
      phone: '(555) 333-3333',
      position: 'Lead Technician',
      department: 'technician',
      hireDate: '2021-06-01',
      salary: '$72,000',
      status: 'active',
      certifications: ['EPA 608', 'NATE Certified', 'R-410A Certified', 'Brazing Certified'],
      completedJobs: 203,
      rating: 4.9,
      address: '789 Expert Blvd, City, ST 34567',
      emergencyContact: 'Susan Anderson - (555) 333-4444',
      notes: 'Team leader with excellent problem-solving skills.'
    },
    {
      id: 4,
      name: 'Chris Miller',
      email: 'chris.miller@hvaccrm.com',
      phone: '(555) 444-4444',
      position: 'HVAC Installer',
      department: 'technician',
      hireDate: '2023-05-20',
      salary: '$48,000',
      status: 'active',
      certifications: ['EPA 608'],
      completedJobs: 45,
      rating: 4.3,
      address: '321 New Tech St, City, ST 45678',
      emergencyContact: 'Lisa Miller - (555) 444-5555',
      notes: 'New hire showing great potential in installations.'
    },
    {
      id: 5,
      name: 'Steve Wilson',
      email: 'steve.wilson@hvaccrm.com',
      phone: '(555) 555-5555',
      position: 'Maintenance Technician',
      department: 'technician',
      hireDate: '2022-11-12',
      salary: '$52,000',
      status: 'on-leave',
      certifications: ['EPA 608', 'Preventive Maintenance Certified'],
      completedJobs: 156,
      rating: 4.5,
      address: '654 Service Circle, City, ST 56789',
      emergencyContact: 'Carol Wilson - (555) 555-6666',
      notes: 'Currently on medical leave. Expected return: March 2024.'
    },
    {
      id: 6,
      name: 'Jessica Roberts',
      email: 'jessica.roberts@hvaccrm.com',
      phone: '(555) 666-6666',
      position: 'Sales Representative',
      department: 'sales',
      hireDate: '2022-08-01',
      salary: '$55,000',
      status: 'active',
      certifications: ['HVAC Sales Certified'],
      completedJobs: 0,
      rating: 4.7,
      address: '987 Sales Drive, City, ST 67890',
      emergencyContact: 'Mark Roberts - (555) 666-7777',
      notes: 'Top performer in lead conversion. Great customer relations.'
    }
  ]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [filterDepartment, setFilterDepartment] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  const getDepartmentColor = (department: string) => {
    switch (department) {
      case 'technician': return '#007bff';
      case 'sales': return '#28a745';
      case 'admin': return '#ffc107';
      case 'management': return '#6f42c1';
      default: return '#6c757d';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#28a745';
      case 'inactive': return '#dc3545';
      case 'on-leave': return '#ffc107';
      default: return '#6c757d';
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const deptMatch = filterDepartment === 'all' || emp.department === filterDepartment;
    const statusMatch = filterStatus === 'all' || emp.status === filterStatus;
    return deptMatch && statusMatch;
  });

  const getAverageRating = () => {
    const sum = filteredEmployees.reduce((acc, emp) => acc + emp.rating, 0);
    return (sum / filteredEmployees.length).toFixed(1);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h1 style={{ color: '#2c3e50', marginBottom: '10px' }}>Employees</h1>
          <p style={{ color: '#6c757d' }}>Manage your team and workforce</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => setShowAddForm(true)}
        >
          + Add Employee
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)' }}>
          <div className="stat-number">{filteredEmployees.length}</div>
          <div className="stat-label">Total Employees</div>
        </div>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)' }}>
          <div className="stat-number">{filteredEmployees.filter(e => e.status === 'active').length}</div>
          <div className="stat-label">Active Employees</div>
        </div>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)' }}>
          <div className="stat-number">{filteredEmployees.filter(e => e.department === 'technician').length}</div>
          <div className="stat-label">Technicians</div>
        </div>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)' }}>
          <div className="stat-number">{getAverageRating()}</div>
          <div className="stat-label">Avg Rating</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <label style={{ marginRight: '10px', fontWeight: '500' }}>Department:</label>
            <select 
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="all">All Departments</option>
              <option value="technician">Technician</option>
              <option value="sales">Sales</option>
              <option value="admin">Admin</option>
              <option value="management">Management</option>
            </select>
          </div>
          <div>
            <label style={{ marginRight: '10px', fontWeight: '500' }}>Status:</label>
            <select 
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="on-leave">On Leave</option>
            </select>
          </div>
        </div>
      </div>

      {showAddForm && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>Add New Employee</h3>
          <form style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            <div className="form-group">
              <label>Full Name *</label>
              <input type="text" required placeholder="Employee full name" />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input type="email" required placeholder="employee@hvaccrm.com" />
            </div>
            <div className="form-group">
              <label>Phone *</label>
              <input type="tel" required placeholder="(555) 123-4567" />
            </div>
            <div className="form-group">
              <label>Position *</label>
              <input type="text" required placeholder="Job title" />
            </div>
            <div className="form-group">
              <label>Department *</label>
              <select required>
                <option value="">Select Department</option>
                <option value="technician">Technician</option>
                <option value="sales">Sales</option>
                <option value="admin">Admin</option>
                <option value="management">Management</option>
              </select>
            </div>
            <div className="form-group">
              <label>Hire Date *</label>
              <input type="date" required />
            </div>
            <div className="form-group">
              <label>Salary</label>
              <input type="text" placeholder="$50,000" />
            </div>
            <div className="form-group">
              <label>Emergency Contact</label>
              <input type="text" placeholder="Name - Phone" />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Address</label>
              <input type="text" placeholder="Full address" />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Certifications</label>
              <input type="text" placeholder="EPA 608, NATE Certified, etc. (comma separated)" />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Notes</label>
              <textarea 
                rows={3} 
                placeholder="Additional notes about the employee"
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  resize: 'vertical'
                }}
              ></textarea>
            </div>
          </form>
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            <button className="btn btn-primary">Save Employee</button>
            <button 
              className="btn btn-secondary"
              onClick={() => setShowAddForm(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <table className="data-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Contact</th>
              <th>Position</th>
              <th>Department</th>
              <th>Hire Date</th>
              <th>Jobs/Rating</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.map((employee) => (
              <tr key={employee.id}>
                <td>
                  <div>
                    <strong>{employee.name}</strong>
                    <br />
                    <small style={{ color: '#6c757d' }}>ID: {employee.id}</small>
                  </div>
                </td>
                <td>
                  <div>
                    {employee.email}
                    <br />
                    <small style={{ color: '#6c757d' }}>{employee.phone}</small>
                  </div>
                </td>
                <td>
                  <div>
                    <strong>{employee.position}</strong>
                    <br />
                    <small style={{ color: '#6c757d' }}>{employee.salary}</small>
                  </div>
                </td>
                <td>
                  <span 
                    className="status"
                    style={{ 
                      backgroundColor: `${getDepartmentColor(employee.department)}20`,
                      color: getDepartmentColor(employee.department)
                    }}
                  >
                    {employee.department}
                  </span>
                </td>
                <td>{employee.hireDate}</td>
                <td>
                  <div>
                    <strong>{employee.completedJobs} jobs</strong>
                    <br />
                    <small style={{ color: '#6c757d' }}>
                      ⭐ {employee.rating}/5.0
                    </small>
                  </div>
                </td>
                <td>
                  <span 
                    className="status"
                    style={{ 
                      backgroundColor: `${getStatusColor(employee.status)}20`,
                      color: getStatusColor(employee.status)
                    }}
                  >
                    {employee.status}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '5px', flexDirection: 'column' }}>
                    <button 
                      style={{ 
                        padding: '4px 8px', 
                        fontSize: '11px', 
                        backgroundColor: '#007bff', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '3px',
                        cursor: 'pointer'
                      }}
                      onClick={() => setSelectedEmployee(employee)}
                    >
                      View
                    </button>
                    <button 
                      style={{ 
                        padding: '4px 8px', 
                        fontSize: '11px', 
                        backgroundColor: '#28a745', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '3px',
                        cursor: 'pointer'
                      }}
                    >
                      Edit
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedEmployee && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: '700px', width: '90%', maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: '#2c3e50', margin: 0 }}>Employee Details</h3>
              <button 
                onClick={() => setSelectedEmployee(null)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '24px', 
                  cursor: 'pointer',
                  color: '#6c757d'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <h4>Personal Information</h4>
                <p><strong>Name:</strong> {selectedEmployee.name}</p>
                <p><strong>Email:</strong> {selectedEmployee.email}</p>
                <p><strong>Phone:</strong> {selectedEmployee.phone}</p>
                <p><strong>Address:</strong> {selectedEmployee.address}</p>
                <p><strong>Emergency Contact:</strong> {selectedEmployee.emergencyContact}</p>
              </div>
              <div>
                <h4>Employment Details</h4>
                <p><strong>Position:</strong> {selectedEmployee.position}</p>
                <p><strong>Department:</strong> {selectedEmployee.department}</p>
                <p><strong>Hire Date:</strong> {selectedEmployee.hireDate}</p>
                <p><strong>Salary:</strong> {selectedEmployee.salary}</p>
                <p><strong>Status:</strong> {selectedEmployee.status}</p>
              </div>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <h4>Performance</h4>
              <p><strong>Completed Jobs:</strong> {selectedEmployee.completedJobs}</p>
              <p><strong>Rating:</strong> ⭐ {selectedEmployee.rating}/5.0</p>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <h4>Certifications</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                {selectedEmployee.certifications.map((cert, index) => (
                  <span 
                    key={index}
                    className="status"
                    style={{ 
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                      fontSize: '12px'
                    }}
                  >
                    {cert}
                  </span>
                ))}
              </div>
            </div>
            
            <div>
              <h4>Notes</h4>
              <p>{selectedEmployee.notes || 'No notes available'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeesPage;